import { useState, useEffect } from 'react';
import { Check, ArrowRight, Sparkles, HelpCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { SEOPage } from '@/components/SEO';

const HERO_BG = "https://customer-assets.emergentagent.com/job_ae021ef0-7027-4ae5-b97f-cd7f1827f457/artifacts/z1lv2e2v_P2341000.JPG";

const FEED_URL = "https://paisley.org.uk/api/sponsor-packages";
const HUB_URL = "https://paisley.org.uk/sponsors";
const TIER_ORDER = ["bronze", "silver", "gold", "platinum"];

const faqs = [
  {
    question: "How long does a sponsorship last?",
    answer: "Our standard sponsorship packages run for 12 months from the date of activation.",
  },
  {
    question: "Can I upgrade my package later?",
    answer: "Absolutely — you can upgrade at any time. Pro-rata adjustments are made for the remaining months.",
  },
  {
    question: "What happens after I sign up?",
    answer: `Sign-up is handled on Paisley.org.uk. Once your payment is confirmed, the team will reach out within 24 hours to gather your details. You'll be live within 48 hours.`,
  },
  {
    question: "Why is sponsorship handled on Paisley.org.uk?",
    answer: "Paisley.org.uk is the single source of truth for our partnership network — your sponsorship there grants visibility across all our affiliated local sites.",
  },
];

const SponsorsPage = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    // No caching — fetch every page load
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(FEED_URL, { method: 'GET', cache: 'no-store' });
        if (!res.ok) throw new Error(`Feed responded ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const incoming = Array.isArray(data.tiers) ? data.tiers : [];
        const ordered = [...incoming].sort(
          (a, b) => TIER_ORDER.indexOf(a.id) - TIER_ORDER.indexOf(b.id),
        );
        setTiers(ordered);
        setErrored(false);
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sponsors-page">
      <SEOPage.Sponsors />
      <Navbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 to-slate-900/95" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-sky-500/20 text-sky-400 border-sky-500/30">
            Partnership Opportunities
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Sponsorship Packages
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-4">
            Choose the perfect tier to showcase your business to Paisley's vibrant community.
          </p>
          <p
            className="text-slate-400 text-sm max-w-2xl mx-auto"
            data-testid="sponsorship-branding-strip"
          >
            Sponsorship managed by{' '}
            <a
              href="https://paisley.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-400 hover:text-sky-300 underline"
              data-testid="branding-strip-link"
            >
              Paisley.org.uk
            </a>{' '}
            — your partnership powers our network of local sites.
          </p>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20 -mt-10" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-8">
                    <div className="h-6 bg-slate-200 rounded w-1/2 mb-4" />
                    <div className="h-12 bg-slate-200 rounded w-1/3 mb-6" />
                    <div className="space-y-3 mb-8">
                      {[1, 2, 3, 4, 5].map((j) => (<div key={j} className="h-4 bg-slate-100 rounded" />))}
                    </div>
                    <div className="h-12 bg-slate-200 rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : errored ? (
            <Card className="max-w-2xl mx-auto" data-testid="sponsors-fallback">
              <CardContent className="p-10 text-center">
                <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  Sponsorship details temporarily unavailable
                </h3>
                <p className="text-slate-600 mb-6">
                  Visit{' '}
                  <a
                    href={HUB_URL}
                    className="text-sky-600 hover:text-sky-700 font-medium underline"
                    data-testid="fallback-hub-link"
                  >
                    paisley.org.uk/sponsors
                  </a>{' '}
                  to view our partnership tiers.
                </p>
                <a href={HUB_URL}>
                  <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="fallback-cta-btn">
                    Go to Paisley.org.uk Sponsors <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {tiers.map((tier) => {
                const isGold = tier.id === 'gold';
                return (
                  <Card
                    key={tier.id}
                    className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-2 bg-white ${
                      isGold
                        ? 'border-2 border-amber-400 shadow-2xl shadow-amber-400/20 lg:scale-105 lg:z-10'
                        : 'border border-slate-200 hover:shadow-xl'
                    }`}
                    data-testid={`pricing-card-${tier.id}`}
                  >
                    {isGold && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-center text-sm font-semibold py-2 z-10">
                        <Sparkles className="inline h-4 w-4 mr-1" /> MOST POPULAR
                      </div>
                    )}
                    <CardHeader className={`pb-0 ${isGold ? 'pt-14' : 'pt-8'}`}>
                      <h3 className="font-heading text-2xl font-bold text-slate-900">
                        {tier.name}
                      </h3>
                      {tier.description && (
                        <p className="text-sm text-slate-500 mt-1">{tier.description}</p>
                      )}
                    </CardHeader>
                    <CardContent className="p-8 pt-4">
                      <div className="flex items-baseline gap-1 mb-8">
                        <span className="font-accent text-5xl font-bold text-slate-900">
                          £{tier.amount}
                        </span>
                        <span className="text-slate-500">/yr</span>
                      </div>

                      <ul className="space-y-3 mb-8 min-h-[180px]">
                        {(tier.benefits || []).map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isGold ? 'bg-amber-100' : 'bg-sky-100'
                            }`}>
                              <Check className={`h-3 w-3 ${isGold ? 'text-amber-600' : 'text-sky-600'}`} />
                            </div>
                            <span className="text-slate-600 text-sm leading-snug">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <a
                        href={HUB_URL}
                        data-testid={`select-tier-${tier.id}`}
                      >
                        <Button
                          className={`w-full py-6 text-base ${
                            isGold
                              ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg shadow-amber-500/25'
                              : 'bg-slate-900 hover:bg-slate-800 text-white'
                          }`}
                        >
                          Select {tier.name}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-900" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              What You Get
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Every partnership unlocks these core benefits across the Paisley network
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: '🎯', title: 'Targeted Reach', desc: 'Connect with locals actively searching for businesses in Paisley' },
              { icon: '📱', title: 'Multi-Platform', desc: 'Visibility across our website, newsletters, and social media' },
              { icon: '📊', title: 'Analytics', desc: 'Monthly reports on your listing views and engagement' },
              { icon: '🤝', title: 'Support', desc: 'Dedicated account manager to maximise your visibility' },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="font-heading text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-sky-100 rounded-2xl mb-4">
              <HelpCircle className="h-7 w-7 text-sky-600" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white rounded-xl border border-slate-200 px-6 data-[state=open]:shadow-lg"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-sky-500" data-testid="sponsors-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-sky-100 text-lg mb-8">
            Sign-up and payment is managed on Paisley.org.uk — the source of truth for our partnership network.
          </p>
          <a href={HUB_URL}>
            <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 px-8" data-testid="bottom-hub-cta">
              Visit Paisley.org.uk/Sponsors <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SponsorsPage;
